/**
 * Recovering the text a turn actually produced.
 *
 * The AI SDK's `onFinish` event extends `StepResult`, so its `text` field is
 * the FINAL step's text — not the turn's. A research-heavy turn that ends on
 * a tool call, or whose last step spends its whole output budget on reasoning
 * tokens, has an empty `text` even when earlier steps streamed real prose that
 * the user watched arrive.
 *
 * chat-v3's server-side save read `response.messages[length - 1]` and had the
 * same blind spot: if that last message carried no `type: 'text'` part it
 * logged `Server-side save skipped: empty assistant text` and returned,
 * discarding the turn. Confirmed live 2026-08-12 — a turn burned 480,181
 * tokens, called `ready_to_answer`, ran ~5 minutes, and the user saw nothing.
 *
 * `steps[]` is the whole turn, so that is what we read.
 */

import type { StreamTextTransform, ToolSet, UIMessageChunk } from 'ai'

/** The canvas write that makes a turn's output durable. */
const CANVAS_TOOL_NAME = 'add_to_canvas'

/**
 * The two fields anything in this file reads off a step, extracted without
 * asserting the step's shape.
 *
 * Every steps-taking function below declares `steps: unknown`, which is the
 * honest type: `steps[]` reaches them from the AI SDK's `onFinish` event and
 * from persisted rows, and each of them documents — in its own comment — what
 * it does when that input is malformed. Those documented behaviours were
 * unreachable from a type-checked caller while the parameter claimed a
 * well-formed array, which is why every test covering them had to lie to the
 * compiler. The narrowing happens here, once.
 */
function stepText(step: unknown): string {
  if (typeof step !== 'object' || step === null || !('text' in step)) return ''
  const { text } = step
  return typeof text === 'string' ? text : ''
}

/**
 * Did this step write to the canvas?
 *
 * Read so a step that wrote the deliverable to the canvas counts as delivery
 * even when it wrote no prose.
 */
function wroteToCanvas(step: unknown): boolean {
  if (typeof step !== 'object' || step === null || !('toolCalls' in step)) return false
  const { toolCalls } = step
  if (!Array.isArray(toolCalls)) return false
  return toolCalls.some((call: unknown) => {
    if (typeof call !== 'object' || call === null || !('toolName' in call)) return false
    return call.toolName === CANVAS_TOOL_NAME
  })
}

/** Minimal shape of a response message, for the fallback path. */
interface ContentPart {
  type?: string
  text?: string
}
interface ResponseMessage {
  role?: string
  content?: unknown
}

/** Joined with a blank line so prose from separate steps doesn't run together. */
const STEP_SEPARATOR = '\n\n'

/**
 * All text the assistant emitted across every step of a turn.
 *
 * Steps that produced no prose (pure tool calls, pure reasoning) contribute
 * nothing rather than empty strings, so the separator never doubles up.
 */
export function collectAssistantText(steps: unknown): string {
  if (!Array.isArray(steps)) return ''
  const chunks: string[] = []
  for (const step of steps) {
    const text = stepText(step).trim()
    if (text) chunks.push(text)
  }
  return chunks.join(STEP_SEPARATOR)
}

/**
 * Fallback for when `steps` is unavailable — walks the response messages.
 *
 * Reads EVERY assistant message rather than only the last, for the same
 * reason `collectAssistantText` reads every step. Tool-role messages carry
 * no text parts, so they contribute nothing either way; the role check is
 * explicit so the intent survives future SDK shape changes.
 */
export function collectTextFromMessages(messages: unknown): string {
  if (!Array.isArray(messages)) return ''
  const chunks: string[] = []
  for (const message of messages as ResponseMessage[]) {
    if (message?.role && message.role !== 'assistant') continue
    const content = message?.content
    if (typeof content === 'string') {
      if (content.trim()) chunks.push(content.trim())
      continue
    }
    if (!Array.isArray(content)) continue
    const text = (content as ContentPart[])
      .filter((part) => part?.type === 'text')
      .map((part) => part?.text ?? '')
      .join('')
    if (text.trim()) chunks.push(text.trim())
  }
  return chunks.join(STEP_SEPARATOR)
}

/**
 * Persisted in place of an assistant reply when a turn genuinely produced no
 * text anywhere.
 *
 * Silence was the actual harm here: the turn ran, cost real tokens and real
 * minutes, and left no trace the user could see or we could later find. A
 * persisted notice means the conversation shows what happened and the row
 * exists to query.
 *
 * Deliberately not written in the assistant's voice — it is a system notice
 * about a failed turn, and phrasing it as the model speaking would imply the
 * model chose to say this.
 */
export const EMPTY_TURN_NOTICE =
  'This turn finished without producing an answer. That usually means a long ' +
  'investigation used up the turn before a reply could be written. Nothing you ' +
  'sent was lost — ask again, or narrow the question, and the conversation ' +
  'carries on from here.'

/** True when the saved content is the notice rather than a real reply. */
export function isEmptyTurnNotice(content: string | null | undefined): boolean {
  return content === EMPTY_TURN_NOTICE
}

/**
 * What an empty turn says when it DID deliver — to the canvas, not the chat.
 *
 * Same system voice as EMPTY_TURN_NOTICE and for the same reason: it is a
 * statement about the turn's shape, not the model speaking. It replaces the
 * notice rather than joining it, because the two claims contradict each other.
 */
export const CANVAS_ONLY_DELIVERY_NOTE =
  'This turn wrote its answer to the canvas rather than the chat.'

/**
 * Whether the turn's LAST step wrote anything.
 *
 * This is the predicate that matters, and it is not the same as "did the turn
 * produce text". The tool loop reserves its final step for the answer
 * (getFinalStepOverrides sets toolChoice:'none' and asks for a synthesis), so
 * that step is where the reply lives. A turn can narrate its plan across the
 * early steps — "Let me dig into the item-level data:" — spend the whole loop
 * on tool calls, and then write nothing at the end. The persisted content is
 * not empty, so every emptiness check passes, and the user is left holding a
 * plan instead of an answer.
 *
 * Observed in production across six users between 2026-05-19 and 2026-08-11.
 * The clearest case: a specific clinical question, 32 tool calls over 20
 * iterations (the cap), 472k cached tokens read, 389 output tokens for the
 * entire turn, and a 200-character reply that only says what it is about to do.
 *
 * Malformed input returns true — unable to judge is not the same as guilty,
 * and a false accusation would append a notice to a turn that answered fine.
 */
export function hasFinalSynthesis(steps: unknown): boolean {
  if (!Array.isArray(steps) || steps.length === 0) return true

  // A canvas write is NOT an answer here. It used to short-circuit this to
  // true, which is the right answer for the notice and the wrong one for the
  // metric: it made artifact-but-silence unmeasurable, so nobody could ask how
  // often it happens. That suppression now lives in shouldShowNotice, and this
  // predicate answers exactly one question — did the final step write a reply.
  const text = stepText(steps[steps.length - 1])
  if (!text.trim()) return false
  return !isOnlyDeferral(text)
}

/** Did this turn leave a durable artifact behind, anywhere in it. */
export function turnWroteToCanvas(steps: unknown): boolean {
  return Array.isArray(steps) && steps.some((step: unknown) => wroteToCanvas(step))
}

/** The same question against the flattened parts the browser holds. */
export function partsWroteToCanvas(parts: unknown): boolean {
  return (
    Array.isArray(parts)
    && parts.some((p) => (p as ContentPart)?.type === `tool-${CANVAS_TOOL_NAME}`)
  )
}

/**
 * Whether to tell the user this turn did not answer them.
 *
 * Separate from the metric on purpose. A turn that wrote a document and then
 * signed off with "let me know what you think" has not failed anybody, and
 * printing "this turn never answered" under real work is a worse error than
 * staying quiet — so an artifact suppresses the notice even though the metric
 * still records that no reply was written.
 *
 * The failure direction is asserted rather than assumed: malformed input makes
 * no accusation, because `hasFinalSynthesis` abstains to true.
 */
export function shouldShowNotice(steps: unknown): boolean {
  return !hasFinalSynthesis(steps) && !turnWroteToCanvas(steps)
}

/** What a turn with no text at all should persist, and what to record about it. */
export interface EmptyTurnOutcome {
  /** The body to save in place of the answer that was never written. */
  body: string
  /**
   * An artifact stood in for the reply. Feeds `canvas_only_delivery`, which
   * could not previously be recorded for the purely silent case at all — that
   * metadata is gated on `!isEmptyTurn`, and a canvas-only turn is always an
   * empty turn.
   */
  canvasOnly: boolean
}

/**
 * The empty-turn body, which is not always an apology.
 *
 * `shouldShowNotice` already knows an artifact buys silence, but the route
 * cannot reach it here: `isEmptyTurn` is `!assistantText`, a canvas-only
 * delivery writes no chat text by definition, and `isEmptyTurn` is tested
 * first. So the suppression was correct and simply never consulted — two of
 * the three empty-turn notices ever emitted were false accusations printed
 * over real documents (prod, 2026-08-25).
 *
 * `canvasLink` is passed in rather than derived, so this stays free of routing
 * and mode knowledge; the caller already holds both.
 *
 * Two directions worth stating, because they are not the same as the notice
 * predicates':
 *  - A MISSING LINK degrades the pointer, it does not restore the accusation.
 *    The artifact's existence does not depend on our ability to address it.
 *  - MALFORMED STEPS fall back to the notice. Everywhere else in this file
 *    "unable to judge" resolves to silence, because a false accusation is the
 *    worse error. Here the alternative is claiming a document that may not
 *    exist and sending the user to an empty canvas, which is worse still.
 */
export function resolveEmptyTurn(steps: unknown, canvasLink?: string | null): EmptyTurnOutcome {
  if (!turnWroteToCanvas(steps)) {
    return { body: EMPTY_TURN_NOTICE, canvasOnly: false }
  }

  const link = typeof canvasLink === 'string' ? canvasLink.trim() : ''
  return {
    body: link ? `${CANVAS_ONLY_DELIVERY_NOTE}\n\n${link}` : CANVAS_ONLY_DELIVERY_NOTE,
    canvasOnly: true,
  }
}

/**
 * The notice decision, client side.
 *
 * The browser's save lands AFTER the server's and overwrites it, so a
 * disagreement here erases the server's verdict. Both halves of the split have
 * to match, not just the metric.
 */
export function shouldShowNoticeFromParts(parts: unknown): boolean {
  return !hasFinalSynthesisFromParts(parts) && !partsWroteToCanvas(parts)
}

/**
 * A sentence that only announces future self-action: "Let me build the
 * playbook.", "I'll now run the reconciliation."
 *
 * `let me know` is deliberately excluded — that is addressed TO the user and
 * routinely closes a perfectly good answer. The leading alternation absorbs
 * the discourse markers these turns habitually open with ("Good — ", "Now ",
 * "Excellent, ") so the classification looks at the actual clause.
 */
const DEFERRAL_CLAUSE =
  /^(?:(?:and|so|ok|okay|good|great|excellent|perfect|now|next)\b[\s,;:—–-]*)*(?:let me(?!\s+know\b)|i'?ll|i\s+will|i'?m\s+going\s+to|i\s+am\s+going\s+to)\b/i

/**
 * A sentence that only asserts having finished gathering: "Now I have
 * everything I need.", "I now have complete intelligence on all 30 companies."
 *
 * These carry no information for the user — they are the model talking to
 * itself about its own readiness — and they are the other half of what a
 * deferring final step is made of. Both recorded production cases open the
 * reserved step with one.
 */
const COMPLETENESS_CLAIM =
  /^(?:(?:and|so|ok|okay|good|great|excellent|perfect|now|next)\b[\s,;:—–-]*)*i(?:'ve|\s+have|\s+now\s+have)\s+(?:everything|all\b|the\s+full\b|complete\b|what\s+i\s+need\b)/i

/**
 * Whether text is nothing but promises and readiness claims.
 *
 * The bias is deliberately toward ACCEPTING: a false accusation puts a "this
 * turn never answered" notice under a reply that answered fine, which is worse
 * than missing one. So a single sentence that is neither a deferral nor a
 * readiness claim is enough to count as an answer — including an honest "I
 * could not confirm that", which is a real answer to the user.
 */
export function isOnlyDeferral(text: unknown): boolean {
  if (typeof text !== 'string') return false
  const sentences = text
    .split(/(?<=[.!?:])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (sentences.length === 0) return false
  return sentences.every((s) => DEFERRAL_CLAUSE.test(s) || COMPLETENESS_CLAIM.test(s))
}

/**
 * The same judgement as `hasFinalSynthesis`, made from a UI message's parts.
 *
 * The browser never sees `steps[]` — it holds one flattened UIMessage whose
 * step boundaries are marked by `step-start` parts. It needs its own copy of
 * this predicate because its `onFinish` saves AFTER the server's and overwrites
 * it, using text rebuilt from those parts. Without agreeing here, the client
 * would erase the notice the server just appended and hand the user the bare
 * preamble a second time.
 *
 * Reasoning parts after the boundary do not count: a final step that spent its
 * budget thinking and wrote nothing is exactly the failure being detected.
 */
export function hasFinalSynthesisFromParts(parts: unknown): boolean {
  if (!Array.isArray(parts)) return true

  const lastBoundary = parts.map((p) => (p as ContentPart)?.type).lastIndexOf('step-start')
  // No boundaries recorded — nothing to reason about, so make no accusation.
  if (lastBoundary === -1) return true
  const finalText = parts
    .slice(lastBoundary + 1)
    .filter((part) => {
      const p = part as ContentPart
      return p?.type === 'text' && typeof p.text === 'string' && p.text.trim().length > 0
    })
    .map((part) => (part as ContentPart).text!.trim())
    .join('\n\n')

  if (!finalText) return false
  return !isOnlyDeferral(finalText)
}

/**
 * Appended when the investigation ran but the answer was never written.
 *
 * Appended, not substituted: the preamble is the only record of what was being
 * attempted, and the tool results behind it cost real minutes. Replacing it
 * would throw away the one useful thing the turn produced. The notice is
 * separated by a blank line so it reads as a system remark rather than as the
 * assistant trailing off.
 */
export const UNFINISHED_TURN_NOTICE =
  '_This turn ended after its investigation without an answer being written — ' +
  'the tool loop used up the turn before a reply could be composed. Nothing you ' +
  'asked was lost. Ask again, or narrow the question, and it will pick up from ' +
  'what was already found._'

/** True when the saved content carries the unfinished-turn notice. */
export function isUnfinishedTurnNotice(content: string | null | undefined): boolean {
  return typeof content === 'string' && content.includes(UNFINISHED_TURN_NOTICE)
}

/**
 * What to persist after a single recovery attempt on a turn that delivered
 * nothing.
 *
 * AB#22735 Bug 1's acceptance criteria asked for the turn to be "retried once
 * automatically with a stronger directive nudge"; only the notice half had been
 * built, so the user was told the turn failed rather than given the answer.
 *
 * Kept pure — the LLM call lives at the call site — so the interesting decision
 * is testable: a retry can defer too, and accepting a second promise would
 * hand the user two of them and mark the turn delivered. The recovery has to
 * clear the same bar as any other final text.
 *
 * The preamble is always kept, for the same reason the notice was an append
 * rather than a substitution: it is the only record of what was attempted.
 */
export function resolveRecoveredText(
  preamble: unknown,
  recovered: unknown,
): { text: string; recovered: boolean; appended: string } {
  const base = typeof preamble === 'string' ? preamble.trim() : ''
  const attempt = typeof recovered === 'string' ? recovered.trim() : ''
  const usable = attempt.length > 0 && !isOnlyDeferral(attempt)

  // `appended` exists so the caller can stream exactly what was persisted.
  // The recovery runs in its own generateText call, so its text never entered
  // the stream and the browser rebuilt its save without it — three stores, three
  // different answers for one turn. Deriving "the answer" a second time at the
  // stream site is how that drift would come back.
  if (usable) return { text: join(base, attempt), recovered: true, appended: attempt }
  // Already carries the notice from an earlier pass — don't stack a second.
  if (isUnfinishedTurnNotice(base)) return { text: base, recovered: false, appended: '' }
  return {
    text: join(base, UNFINISHED_TURN_NOTICE),
    recovered: false,
    appended: UNFINISHED_TURN_NOTICE,
  }
}

/**
 * The three UI-message-stream frames a recovered answer is made of.
 *
 * Derived from the SDK's own chunk union rather than hand-written, so these
 * are assignable to `UIMessageStreamWriter.write()` with no assertion at the
 * call site, and a chunk rename upstream fails to compile here — next to the
 * frames it names — instead of silently widening what the writer accepts.
 */
export type RecoveredTextPart = Extract<
  UIMessageChunk,
  { type: 'text-start' | 'text-delta' | 'text-end' }
>

/**
 * The UI-message-stream frames that add a recovered answer to the live message.
 *
 * Written straight to the stream writer rather than through streamText, because
 * the recovery is a separate call whose output the merged stream never sees.
 * With these frames the client's `message.parts` gains the answer, so its own
 * predicate sees a final step that wrote something and its save stops
 * contradicting the server's.
 *
 * Nothing is emitted for blank text: a start/end pair with no delta would add an
 * empty text part, which renders as a blank markdown block.
 */
export function recoveredTextParts(
  id: string,
  text: string | null | undefined,
): ReadonlyArray<RecoveredTextPart> {
  if (!id || typeof text !== 'string' || !text.trim()) return []
  return [
    { type: 'text-start', id },
    { type: 'text-delta', id, delta: text },
    { type: 'text-end', id },
  ]
}

function join(a: string, b: string): string {
  return a ? `${a}${STEP_SEPARATOR}${b}` : b
}

/**
 * Stream parts that carry assistant prose. Reasoning has its own part types and
 * is deliberately not in this set — a turn can burn its whole budget on
 * reasoning and still emit no text, which is one of the ways this bug happens.
 */
const TEXT_PART_TYPES = new Set(['text-start', 'text-delta', 'text-end'])

/**
 * A `streamText` transform that deletes the assistant's prose from the stream.
 *
 * This is where the rehearsal belongs. Forcing the empty turn inside `onFinish`
 * only suppresses what gets *persisted* — the text has already streamed to the
 * browser by then, so the screen shows a real answer while the database stores
 * the notice. That divergence is an artifact of the switch, not a property of
 * the bug, and it leaves the client-side path completely unexercised.
 *
 * Removing the text parts here instead reproduces the actual condition: no text
 * reaches the browser, and none reaches `steps[].text` either, so the ordinary
 * production code path runs on its own. The switch changes the input; it does
 * not branch the logic under test.
 *
 * Everything else — tool calls, tool results, reasoning, step boundaries — is
 * passed through untouched, because the turn being simulated is a real turn
 * that did real work and merely failed to say anything.
 */
export function dropAssistantTextTransform<
  TOOLS extends ToolSet,
>(): StreamTextTransform<TOOLS> {
  return () =>
    new TransformStream({
      transform(part, controller) {
        if (TEXT_PART_TYPES.has(part?.type)) return
        controller.enqueue(part)
      },
    })
}

/**
 * Whether this process should rehearse the empty turn.
 *
 * The empty turn is rare and cannot be provoked on demand — it needs a long
 * investigation to exhaust the output budget at exactly the wrong moment, which
 * made the recovery path the one part of this file nobody could exercise. That
 * is how it stayed broken. When this returns true, chat-v3 swaps its stream
 * transform for `dropAssistantTextTransform`, and everything downstream then
 * behaves normally against a turn that genuinely has no text.
 *
 * Two independent locks, both of which must be opened deliberately:
 *
 *   1. `CHAT_DEV_FORCE_EMPTY_TURN` must be set. Nothing happens without it.
 *   2. The runtime must not look like the deployed one — either a non-production
 *      NODE_ENV, or an explicit `ENVIRONMENT=development`.
 *
 * The second lock is not simply `NODE_ENV !== 'production'` because the local
 * rig runs the *production image* (frontend/Dockerfile bakes NODE_ENV) — a
 * NODE_ENV-only gate would be inert in the one place built for rehearsing.
 * Compose sets ENVIRONMENT=development; the deployed frontend sets neither this
 * nor the flag (checked against its App Service settings, 2026-08-13), so
 * turning this on in production would take two separate deliberate acts.
 */
/**
 * Rehearses the unfinished turn: prose flows for the first `keepSteps` steps,
 * then stops.
 *
 * The fully-silent variant above reproduces a turn that said nothing at all —
 * two sightings, never reproducible on demand. The failure people actually hit
 * is this one: a plan in the early steps, tool calls for the rest of the loop,
 * and no answer at the end. Two attempts to provoke it with genuinely hard
 * prompts both ended with a real synthesis, so the only way to watch the
 * recovery work is to silence the closing step deliberately.
 *
 * Step boundaries are counted from `finish-step` parts, so the tool calls,
 * results and boundaries all survive — what is rehearsed is a real turn that
 * stopped short, not a hollow one.
 */
export function dropTextAfterStepTransform<TOOLS extends ToolSet>(
  keepSteps: number,
): StreamTextTransform<TOOLS> {
  return () => {
    let stepsSeen = 0
    return new TransformStream({
      transform(part, controller) {
        const type = part?.type
        if (TEXT_PART_TYPES.has(type) && stepsSeen >= keepSteps) return
        if (type === 'finish-step') stepsSeen += 1
        controller.enqueue(part)
      },
    })
  }
}

export function shouldForceEmptyTurn(
  env: {
    NODE_ENV?: string
    ENVIRONMENT?: string
    CHAT_DEV_FORCE_EMPTY_TURN?: string
  } = process.env,
): boolean {
  const flag = env.CHAT_DEV_FORCE_EMPTY_TURN
  if (flag !== '1' && flag !== 'true' && flag !== 'unfinished') return false
  if (env.NODE_ENV !== 'production') return true
  return env.ENVIRONMENT === 'development'
}

/**
 * Which rehearsal the switch is asking for.
 *
 * 'unfinished' reproduces the failure seen in production — a plan, then
 * silence. '1'/'true' reproduces the fully-textless turn. 'off' whenever the
 * two locks in shouldForceEmptyTurn are not both open.
 */
export function emptyTurnRehearsalMode(
  env: {
    NODE_ENV?: string
    ENVIRONMENT?: string
    CHAT_DEV_FORCE_EMPTY_TURN?: string
  } = process.env,
): 'off' | 'all' | 'unfinished' {
  if (!shouldForceEmptyTurn(env)) return 'off'
  return env.CHAT_DEV_FORCE_EMPTY_TURN === 'unfinished' ? 'unfinished' : 'all'
}
